import { useEffect, useState } from "react";
import { Network, UserPlus, Loader2, Mail, Phone as PhoneIcon, Clock, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";

/**
 * Phase 4.18 — Vermittler-Netzwerk aufbauen.
 *
 * Vermittler lädt andere Personen ein, ebenfalls Vermittler zu werden.
 * Beim erfolgreichen Beitritt wird der einladende Vermittler als Mentor
 * verknüpft und bekommt Anteil an deren Vermittlungs-Punkten (Tier-System
 * folgt in einer späteren Phase). Aktuell: Einladungs-Form + Historie.
 */
export default function VermittlerNetzwerkCard() {
    const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", note: "" });
    const [submitting, setSubmitting] = useState(false);
    const [invitations, setInvitations] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(true);
    const [formOpen, setFormOpen] = useState(false);
    const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const load = async () => {
        try {
            const r = await api.get("/vermittler/network/invitations");
            setInvitations(r.data.items || []);
        } catch { /* offline */ }
        finally { setLoadingHistory(false); }
    };
    useEffect(() => { load(); }, []);

    const submit = async () => {
        if (!form.first_name.trim() || !form.email.trim()) {
            toast.error("Vorname und E-Mail sind Pflicht");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/vermittler/network/invite", form);
            toast.success("Einladung versendet — Empfänger wird kontaktiert");
            setForm({ first_name: "", last_name: "", email: "", phone: "", note: "" });
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Einladung fehlgeschlagen");
        } finally {
            setSubmitting(false);
        }
    };

    const stats = {
        pending: invitations.filter((i) => i.status === "pending").length,
        joined: invitations.filter((i) => i.status === "joined").length,
        total: invitations.length,
    };

    return (
        <div className="bg-white border border-gray-200 rounded-sm" data-testid="vermittler-netzwerk-card">
            <button
                type="button"
                onClick={() => setFormOpen((o) => !o)}
                className="w-full flex items-center gap-3 p-5 text-left hover:bg-gray-50 transition-colors"
                aria-expanded={formOpen}
                data-testid="netzwerk-toggle"
            >
                <Network className="w-5 h-5 text-[#FFCC00] shrink-0" />
                <div className="flex-1 min-w-0">
                    <h3 className="font-display font-bold text-base">Vermittler-Netzwerk aufbauen</h3>
                    <p className="text-xs text-gray-600 mt-0.5">
                        {formOpen
                            ? "Laden Sie Personen ein, ebenfalls Vermittler zu werden — Sie erhalten anteilige Punkte aus deren Erfolgen."
                            : `${stats.total} Einladung${stats.total === 1 ? "" : "en"} versendet · ${stats.joined} beigetreten — Klicken zum Einladen`}
                    </p>
                </div>
                <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform shrink-0 ${formOpen ? "rotate-180" : ""}`} />
            </button>

            {formOpen && (
            <div className="px-6 pb-6" data-testid="netzwerk-form">
                {/* Mini-Stats */}
                <div className="grid grid-cols-3 gap-2 mb-4" data-testid="netzwerk-stats">
                <div className="border border-gray-200 rounded-sm p-2 text-center">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Eingeladen</div>
                    <div className="text-lg font-black font-mono-num mt-0.5">{stats.total}</div>
                </div>
                <div className="border border-gray-200 rounded-sm p-2 text-center">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Offen</div>
                    <div className="text-lg font-black font-mono-num mt-0.5 text-amber-700">{stats.pending}</div>
                </div>
                <div className="border border-gray-200 rounded-sm p-2 text-center">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Beigetreten</div>
                    <div className="text-lg font-black font-mono-num mt-0.5 text-green-700">{stats.joined}</div>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                    <Label className="text-[11px] uppercase tracking-wider font-bold">Vorname *</Label>
                    <Input value={form.first_name} onChange={(e) => update("first_name", e.target.value)} placeholder="z. B. Anna" className="rounded-sm" data-testid="netzwerk-first-name" />
                </div>
                <div>
                    <Label className="text-[11px] uppercase tracking-wider font-bold">Nachname</Label>
                    <Input value={form.last_name} onChange={(e) => update("last_name", e.target.value)} placeholder="z. B. Becker" className="rounded-sm" data-testid="netzwerk-last-name" />
                </div>
                <div>
                    <Label className="text-[11px] uppercase tracking-wider font-bold">E-Mail *</Label>
                    <Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="anna@example.com" className="rounded-sm" data-testid="netzwerk-email" />
                </div>
                <div>
                    <Label className="text-[11px] uppercase tracking-wider font-bold">Telefon</Label>
                    <Input value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="+49 …" className="rounded-sm" data-testid="netzwerk-phone" />
                </div>
            </div>
            <div className="mt-3">
                <Label className="text-[11px] uppercase tracking-wider font-bold">Persönliche Nachricht (optional)</Label>
                <Textarea value={form.note} onChange={(e) => update("note", e.target.value)} rows={2} placeholder="z. B. Hi Anna, ich hab' bei TwoWheels schon 3 Verkäufe vermittelt — du wärst genauso gut darin." className="rounded-sm text-sm" data-testid="netzwerk-note" />
            </div>
            <Button
                onClick={submit}
                disabled={submitting}
                className="mt-3 w-full sm:w-auto bg-[#111827] hover:bg-black text-white font-bold rounded-sm uppercase tracking-wider"
                data-testid="netzwerk-submit"
            >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><UserPlus className="w-4 h-4 mr-1.5" />Einladung senden</>}
            </Button>
            </div>
            )}

            <div className="px-6 pb-6 pt-4 border-t border-gray-100">
                <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-2">
                    Ihre Einladungen
                </div>
                {loadingHistory ? (
                    <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                ) : invitations.length === 0 ? (
                    <div className="text-xs text-gray-500" data-testid="netzwerk-empty">
                        Noch keine Einladungen versendet.
                    </div>
                ) : (
                    <ul className="divide-y divide-gray-100 max-h-60 overflow-auto" data-testid="netzwerk-history">
                        {invitations.slice(0, 12).map((inv) => (
                            <li key={inv.id} className="py-2 flex items-start gap-2" data-testid={`netzwerk-invite-${inv.id}`}>
                                <div className="w-8 h-8 rounded-sm bg-[#FFCC00]/20 flex items-center justify-center font-bold text-xs text-[#111827] shrink-0">
                                    {(inv.first_name || "?").charAt(0).toUpperCase()}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-bold truncate">{[inv.first_name, inv.last_name].filter(Boolean).join(" ")}</div>
                                    <div className="text-[11px] text-gray-500 flex items-center gap-2 truncate">
                                        <span className="inline-flex items-center gap-0.5"><Mail className="w-3 h-3" /> {inv.email}</span>
                                        {inv.phone && <span className="inline-flex items-center gap-0.5"><PhoneIcon className="w-3 h-3" /> {inv.phone}</span>}
                                    </div>
                                    <div className="text-[10px] text-gray-400 font-mono-num flex items-center gap-1 mt-0.5">
                                        <Clock className="w-3 h-3" /> {new Date(inv.created_at).toLocaleString("de-DE")}
                                    </div>
                                </div>
                                <span className={`text-[10px] uppercase tracking-wider font-bold shrink-0 ${inv.status === "joined" ? "text-green-700" : "text-amber-700"}`}>
                                    {inv.status === "pending" ? "Eingeladen" : inv.status === "joined" ? "Beigetreten" : inv.status}
                                </span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}
